import { useAuth } from '@/hooks/useAuth';
import { collection, query } from 'firebase/firestore';
import { Loader2, Trash2, Search, FileSignature } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { Contract } from '@/lib/types';
import { ContractCard } from '@/components/contratos/ContractCard';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { deleteContract } from '@/lib/actions';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { sanitizeFirestoreData, cn } from '@/lib/utils';
import { motion } from 'framer-motion';

type Filter = 'all' | 'pending' | 'signed' | 'rejected';

export default function AdminContratosPage() {
  const { user, userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet');
  }, [user, userData, loading, navigate]);

  // Todos los contratos (pendientes, firmados y rechazados)
  const contractsQuery = useMemo(() => query(collection(db, 'contracts')), []);
  const { data: rawContracts, isLoading: areContractsLoading } = useCollection<Contract>(contractsQuery);

  const contracts = useMemo(() => (rawContracts || []).map((c) => sanitizeFirestoreData(c) as Contract), [rawContracts]);

  const counts = useMemo(() => ({
    all: contracts.length,
    pending: contracts.filter((c) => c.status === 'pending').length,
    signed: contracts.filter((c) => c.status === 'signed').length,
    rejected: contracts.filter((c) => c.status === 'rejected').length,
  }), [contracts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contracts
      .filter((c) => filter === 'all' || c.status === filter)
      .filter((c) => !q || `${c.emitterName} ${c.recipientName} ${c.concept}`.toLowerCase().includes(q))
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [contracts, filter, search]);

  const handleDeleteContract = async (contractId: string) => {
    setIsSubmitting(true);
    const result = await deleteContract(contractId);
    if (result.success) { toast({ title: 'Éxito', description: result.message }); setSelectedContract(null); }
    else toast({ title: 'Error', description: result.error, variant: 'destructive' });
    setIsSubmitting(false);
  };

  if (loading || areContractsLoading) {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Cargando gestión de contratos...</p>
      </div>
    );
  }

  const STATUS_COLOR: Record<string, string> = {
    pending: 'bg-amber-500/20 text-amber-400',
    signed: 'bg-green-500/20 text-green-400',
    rejected: 'bg-red-500/20 text-red-400',
  };
  const STATUS_LABEL: Record<string, string> = { pending: 'Pendiente', signed: 'Firmado', rejected: 'Rechazado' };

  return (
    <div className="space-y-6 pb-12">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-sky-500/15 via-card/40 to-card/20 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl border border-sky-400/30 bg-sky-500/15 text-sky-300"><FileSignature className="h-9 w-9" /></div>
          <div>
            <h1 className="font-headline text-4xl font-bold md:text-5xl">Gestión de Contratos</h1>
            <p className="mt-2 text-muted-foreground">Todos los contratos y colaboraciones del servidor: pendientes, firmados y rechazados.</p>
          </div>
        </div>
      </motion.div>

      {/* Filtros + búsqueda */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-xl border border-white/10 bg-card/40 p-1">
          {(['all', 'pending', 'signed', 'rejected'] as Filter[]).map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={cn('rounded-lg px-3 py-1.5 text-sm font-medium transition-colors', filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
              {f === 'all' ? 'Todos' : STATUS_LABEL[f]} <span className="opacity-60">({counts[f]})</span>
            </button>
          ))}
        </div>
        <div className="relative ml-auto max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por emisor, destinatario o concepto..." className="pl-9" />
        </div>
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((contract) => (
            <div key={contract.id} className="group relative">
              <Dialog onOpenChange={(open) => !open && setSelectedContract(null)}>
                <DialogTrigger asChild>
                  <button onClick={() => setSelectedContract(contract)} className="h-full w-full text-left">
                    <ContractCard contract={contract} perspective="admin" />
                  </button>
                </DialogTrigger>
              </Dialog>
              <Badge className={cn('absolute left-2 top-2', STATUS_COLOR[contract.status])}>{STATUS_LABEL[contract.status]}</Badge>
              <div className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="destructive" className="h-8 w-8" disabled={isSubmitting}><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar este contrato?</AlertDialogTitle>
                      <AlertDialogDescription>Esta acción es permanente y eliminará el contrato.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDeleteContract(contract.id)} className="bg-destructive hover:bg-destructive/80">Confirmar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-muted-foreground">
          <FileSignature className="mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">No hay contratos{search || filter !== 'all' ? ' que coincidan con el filtro' : ''}.</p>
        </div>
      )}

      {selectedContract && (
        <Dialog open={!!selectedContract} onOpenChange={(open) => !open && setSelectedContract(null)}>
          <DialogContent className="h-auto max-h-[90vh] w-full max-w-md p-0">
            <ContractCard contract={selectedContract} perspective="admin" isDetailView />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
